import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { Input } from '../components/ui/input';
import { FolderKanban, CheckSquare, User, Search } from 'lucide-react';

const typeConfig = {
  project: { icon: FolderKanban, color: '#0073EA', label: 'Projects' },
  task: { icon: CheckSquare, color: '#00C875', label: 'Tasks' },
  user: { icon: User, color: '#A25DDC', label: 'Team' },
};

export const GlobalSearch = ({ triggerClassName = '', triggerWidthClass = 'max-w-[220px]', mobileIcon = false } = {}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const blurTimeout = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        setShowResults(true);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/search?q=${encodeURIComponent(query.trim())}&limit=20`);
        setResults(res.data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = useCallback((item) => {
    setQuery('');
    setShowResults(false);
    navigate(item.href);
  }, [navigate]);

  // Group results by type
  const grouped = results.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const showPanel = showResults && (query.trim() || loading || results.length > 0);

  if (mobileIcon) {
    return (
      <div className="relative" data-testid="global-search-mobile">
        <button
          type="button"
          onClick={() => {
            setShowResults(true);
            setTimeout(() => inputRef.current?.focus(), 100);
          }}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-[#676879] dark:text-[#9CA0AF] hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30]"
          data-testid="global-search-mobile-btn"
          aria-label="Search"
        >
          <Search className="w-4.5 h-4.5" />
        </button>
        {showResults && (
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setShowResults(false)}>
            <div className="absolute top-2 left-2 right-2 bg-white dark:bg-[#1e1f23] rounded-xl shadow-2xl border border-[#E6E9EF] dark:border-[#3a3b40] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-3 pt-3 pb-2">
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA0AF] pointer-events-none" />
                    <input
                      ref={inputRef}
                      autoFocus
                      placeholder="Search..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      style={{ paddingLeft: '2.25rem', fontSize: '16px' }}
                      className="w-full h-10 bg-[#F6F7FB] dark:bg-[#2a2b30] border border-[#E6E9EF] dark:border-[#3a3b40] rounded-md pr-3 focus:outline-none focus:ring-1 focus:ring-ring text-foreground placeholder:text-muted-foreground"
                      data-testid="global-search-mobile-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowResults(false); setQuery(''); }}
                    className="h-10 px-3 text-sm font-medium text-[#0073EA] hover:text-[#0060B9] flex-shrink-0"
                    data-testid="global-search-mobile-close"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {loading && <div className="py-6 text-center text-sm text-[#676879]">Searching...</div>}
                {!loading && query.trim() && results.length === 0 && (
                  <div className="py-6 text-center text-sm text-[#676879]">No results for "{query}"</div>
                )}
                {!loading && !query.trim() && (
                  <div className="py-6 text-center text-sm text-[#676879]">Start typing to search</div>
                )}
                {!loading && Object.entries(grouped).map(([type, items], idx) => {
                  const config = typeConfig[type] || typeConfig.task;
                  const Icon = config.icon;
                  return (
                    <div key={type} className={`${idx > 0 ? 'border-t border-[#E6E9EF] dark:border-[#3a3b40]' : ''}`}>
                      <div className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-wide text-[#9CA0AF]">{config.label}</div>
                      <div className="pb-2">
                        {items.map((item) => (
                          <button
                            key={item.id}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] text-left"
                            onClick={() => {
                              setShowResults(false);
                              setQuery('');
                              navigate(item.type === 'project' ? `/projects/${item.id}` : item.type === 'user' ? `/team/${item.id}` : `/tasks?taskId=${item.id}`);
                            }}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" style={{ color: config.color }} />
                            <span className="text-sm text-[#323338] dark:text-white truncate">{item.name || item.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative w-full ${triggerWidthClass} ${triggerClassName}`} data-testid="global-search">
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA0AF] pointer-events-none z-10" />
        <Input
          ref={inputRef}
          placeholder="Search projects, tasks, people..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!showResults) setShowResults(true);
          }}
          onFocus={() => {
            if (blurTimeout.current) clearTimeout(blurTimeout.current);
            setShowResults(true);
          }}
          onBlur={() => {
            blurTimeout.current = setTimeout(() => setShowResults(false), 150);
          }}
          style={{ paddingLeft: '2.25rem' }}
          className="pr-14 h-9 text-sm bg-[#F6F7FB] dark:bg-[#2a2b30] border-[#E6E9EF] dark:border-[#3a3b40] focus-visible:ring-0 focus-visible:ring-offset-0"
          data-testid="global-search-input"
        />
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-white dark:bg-[#1e1f23] border border-[#E6E9EF] dark:border-[#3a3b40] rounded absolute right-2 top-1/2 -translate-y-1/2">
          <span className="text-xs">&#8984;</span>K
        </kbd>
      </div>

      {showPanel && (
        <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-[#1e1f23] border border-[#E6E9EF] dark:border-[#3a3b40] rounded-lg shadow-lg z-50 max-h-[380px] overflow-y-auto">
          {loading && (
            <div className="py-6 text-center text-sm text-[#676879]">Searching...</div>
          )}
          {!loading && query.trim() && results.length === 0 && (
            <div className="py-6 text-center text-sm text-[#676879]">No results found for "{query}"</div>
          )}
          {!loading && !query.trim() && (
            <div className="py-6 text-center text-sm text-[#676879]">
              Start typing to search across projects, tasks, and team members
            </div>
          )}
          {!loading && Object.entries(grouped).map(([type, items], idx) => {
            const config = typeConfig[type] || typeConfig.task;
            const Icon = config.icon;
            return (
              <div key={type} className={`${idx > 0 ? 'border-t border-[#E6E9EF] dark:border-[#3a3b40]' : ''}`}>
                <div className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-wide text-[#9CA0AF]">
                  {config.label}
                </div>
                <div className="pb-2">
                  {items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(item)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F6F7FB] dark:hover:bg-[#2a2b30] flex items-center gap-2"
                      data-testid={`search-result-${item.id}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: config.color }} />
                      <div className="flex-1 min-w-0">
                        {item.friendly_id && (
                          <span className="font-mono text-[11px] text-[#0073EA] mr-1.5">{item.friendly_id}</span>
                        )}
                        <span className="font-medium text-[#323338] dark:text-[#e0e0e0]">{item.title}</span>
                        {item.subtitle && (
                          <span className="ml-2 text-xs text-[#676879] dark:text-[#9CA0AF]">{item.subtitle}</span>
                        )}
                      </div>
                      {item.status && (
                        <span className={`text-xs px-1.5 py-0.5 rounded status-${item.status.replace(/\s+/g, '_').toLowerCase()}`}>
                          {item.status}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
